import type { EChartsOption } from 'echarts'
import type { DatasetOption } from 'echarts/types/dist/shared'

import { ageIndex } from '../buildMarkArea'
import { retirementDots } from '../buildMarkPoint'
import type { ChartTheme } from '../chartTheme'
import { axisMoneyFormatter } from '../formatters'
import { withLabelPlacement } from '../milestoneLabelPlacement'
import { niceAxis } from '../niceAxis'
import { wealthRunsMatch } from '../runsMatch'
import type {
  CashflowChartSource,
  ChartMilestone,
  ChartRun,
  WealthChartSource,
} from '../types'
import { buildLegendBase, buildTooltipBase } from './base'
import { GRID_LEFT, GRID_RIGHT, HEADER_LINE } from './plotStyle'
import {
  buildCashflowSeries,
  buildWealthSeries,
  type RunVisibility,
} from './series'

/** Steps up the y axis, which is one fewer than the lines drawn. */
const AXIS_STEPS = 4

/** The highest figure either wealth line reaches, over every run drawn. */
const highestWealth = (runs: WealthChartSource[]): number =>
  Math.max(
    0,
    ...runs.flatMap(run =>
      run.flatMap(point => [point.networth ?? 0, point.liquidAssets ?? 0])
    )
  )

/**
 * The highest figure the cashflow plot reaches, over every run drawn.
 *
 * The three outflows are stacked, so what the axis has to hold is their sum
 * against the income line beside it, not any one of them.
 */
const highestCashflow = (runs: CashflowChartSource[]): number =>
  Math.max(
    0,
    ...runs.flatMap(run =>
      run.flatMap(point => [
        point.income ?? 0,
        (point.incomeTaxes ?? 0) +
          (point.expenses ?? 0) +
          (point.contributions ?? 0),
      ])
    )
  )

/** Room under the plot for the age labels, and nothing after them. */
const AXIS_HEIGHT = 28

/** Air above the legend, which is all the header this canvas carries. */
const LEGEND_TOP_PAD = 8

/** What the legend takes, plus the air above it. */
const HEADER_HEIGHT = LEGEND_TOP_PAD + HEADER_LINE

const BASELINE_SUFFIX = ' (baseline)'

const WEALTH_NAMES = ['Net Worth', 'Liquid Assets'] as const
const CASHFLOW_NAMES = ['Income', 'Taxes', 'Expenses', 'Contributions'] as const

const stripBaselineSuffix = (name: string): string =>
  name.endsWith(BASELINE_SUFFIX) ? name.slice(0, -BASELINE_SUFFIX.length) : name

const forWealth = (m: ChartMilestone): boolean => m.chart !== 'cashflow'
const forCashflow = (m: ChartMilestone): boolean => m.chart !== 'wealth'

export type PlotChart = 'wealth' | 'cashflow'

export type PlotChartOverlay = {
  baselineWealth: WealthChartSource
  baselineCashflow: CashflowChartSource
  baselineMilestones?: ChartMilestone[]
  /**
   * Which run, or runs, this chart draws. Defaults to the scenario.
   *
   * `both` draws the plan as it stands behind the scenario, faded — on the
   * wealth chart only, where seventy stacked columns leave a second run no room
   * to be read in.
   */
  run?: ChartRun
}

export type PlotChartParams = {
  /** Which of the two this canvas draws. The other gets a canvas of its own. */
  chart: PlotChart
  wealth: WealthChartSource
  cashflow: CashflowChartSource
  theme: ChartTheme
  milestones?: ChartMilestone[]
  overlay?: PlotChartOverlay
  /** Drops the hover tooltip, because a tap opens the year breakdown instead. */
  interactive?: boolean
  selectedAge?: string
}

/**
 * One chart on a canvas of its own, where `combined` draws both on one.
 *
 * A page has to sit beside the other page or under it without the canvas being
 * rebuilt, which one shared canvas cannot do. The two builders share their
 * style constants so they still read as one chart.
 *
 * No title: it is drawn above the panel in the app's own type, where it stays
 * put while the panel shrinks.
 */
export const buildPlotChartOption = ({
  chart,
  wealth,
  cashflow,
  theme,
  milestones = [],
  overlay,
  interactive = false,
  selectedAge,
}: PlotChartParams): EChartsOption => {
  const isWealth = chart === 'wealth'
  const run = overlay?.run ?? 'scenario'

  /*
   * The wealth chart alone. Seventy stacked columns leave about four points
   * each, so a second run behind them is a thicket rather than a comparison —
   * where two more lines on a line chart are read at a glance.
   *
   * Dropped as well where the two runs draw the same picture: a second run
   * exactly under the first is a line nobody can see.
   */
  const ghosts =
    run === 'both' &&
    isWealth &&
    overlay !== undefined &&
    !wealthRunsMatch(wealth, overlay.baselineWealth)

  const scenarioVisibility: RunVisibility =
    run === 'baseline' ? 'hidden' : 'lead'
  const baselineVisibility: RunVisibility =
    run === 'baseline' ? 'lead' : ghosts ? 'ghost' : 'hidden'

  const source = isWealth ? wealth : cashflow
  // Over both runs, always: an axis that moves as the reader presses the
  // button makes them rescale two pictures in their head.
  const axis = isWealth
    ? niceAxis(
        highestWealth([wealth, ...(overlay ? [overlay.baselineWealth] : [])]),
        AXIS_STEPS
      )
    : niceAxis(
        highestCashflow([
          cashflow,
          ...(overlay ? [overlay.baselineCashflow] : []),
        ]),
        AXIS_STEPS
      )
  const ages = source.map(point => point.age)
  const selectedIndex = ageIndex(ages, selectedAge)
  const forThisChart = isWealth ? forWealth : forCashflow
  const plotted = milestones.filter(forThisChart)

  const scenarioSeries = isWealth
    ? buildWealthSeries(
        theme,
        withLabelPlacement(plotted, ages),
        scenarioVisibility,
        0,
        '',
        undefined,
        selectedIndex,
        retirementDots(
          wealth,
          withLabelPlacement(plotted, ages),
          ghosts ? overlay?.baselineWealth : undefined,
          axis?.max
        )
      )
    : buildCashflowSeries(
        theme,
        withLabelPlacement(plotted, ages),
        scenarioVisibility,
        0,
        'scenario',
        '',
        undefined,
        selectedIndex
      )

  const baselineSource = overlay
    ? isWealth
      ? overlay.baselineWealth
      : overlay.baselineCashflow
    : undefined

  const baselineAges = baselineSource?.map(point => point.age) ?? []
  // The open year is the same column in either run, so the baseline carries the
  // band too. Only one of the two is ever visible, and without this the band
  // vanished the moment the reader asked to see the plan as it stands.
  const baselineSelectedIndex = ageIndex(baselineAges, selectedAge)

  const baselineSeries =
    overlay && baselineSource
      ? isWealth
        ? buildWealthSeries(
            theme,
            withLabelPlacement(
              (overlay.baselineMilestones ?? []).filter(forThisChart),
              baselineAges
            ),
            baselineVisibility,
            1,
            BASELINE_SUFFIX,
            undefined,
            baselineSelectedIndex,
            retirementDots(
              overlay.baselineWealth,
              withLabelPlacement(
                (overlay.baselineMilestones ?? []).filter(forThisChart),
                baselineAges
              ),
              undefined,
              axis?.max
            )
          )
        : buildCashflowSeries(
            theme,
            withLabelPlacement(
              (overlay.baselineMilestones ?? []).filter(forThisChart),
              baselineAges
            ),
            baselineVisibility,
            1,
            'baseline',
            BASELINE_SUFFIX,
            undefined,
            baselineSelectedIndex
          )
      : []

  const dataset: DatasetOption | DatasetOption[] =
    overlay && baselineSource
      ? [{ source }, { source: baselineSource }]
      : { source }

  const scenarioNames = isWealth ? WEALTH_NAMES : CASHFLOW_NAMES
  const legendNames = overlay
    ? run !== 'baseline'
      ? [...scenarioNames]
      : scenarioNames.map(name => `${name}${BASELINE_SUFFIX}`)
    : [...scenarioNames]

  const axisLabel = {
    fontFamily: theme.fontFamily,
    fontSize: theme.fontSize,
    color: theme.axis.labelColor,
  }

  return {
    textStyle: {
      fontFamily: theme.fontFamily,
      fontSize: theme.fontSize,
    },
    legend: {
      ...buildLegendBase(theme),
      top: 0,
      // Centred: the plot has no title beside it on this canvas, so a legend
      // pushed to the left hangs off one edge of a chart that is otherwise
      // symmetrical.
      left: 'center',
      right: undefined,
      padding: [LEGEND_TOP_PAD, 0, 0, 0] as [number, number, number, number],
      data: legendNames,
      formatter: overlay ? stripBaselineSuffix : undefined,
    },
    grid: {
      top: HEADER_HEIGHT,
      left: GRID_LEFT,
      right: GRID_RIGHT,
      bottom: AXIS_HEIGHT,
    },
    xAxis: {
      type: 'category',
      axisLabel,
      axisLine: {
        lineStyle: { color: theme.axis.lineColor },
      },
      axisTick: { show: false },
    },
    yAxis: {
      type: 'value',
      min: 0,
      /*
       * An exact number of rules, which `splitNumber` cannot hold: it is a
       * hint, and ECharts takes as many round steps as the data needs. The
       * step and the top are stated instead — see `niceAxis` for how the step
       * stays round while doing it.
       */
      ...(axis ?? {}),
      splitLine: {
        lineStyle: { color: theme.axis.splitLineColor },
      },
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        ...axisLabel,
        formatter: axisMoneyFormatter,
      },
    },
    series: [...scenarioSeries, ...baselineSeries],
    // `moveOnMouseMove` off for the reason above: a pinch spreads the years
    // out, and a drag still pages.
    dataZoom: [{ type: 'inside', moveOnMouseMove: false }],
    dataset,
    ...(interactive ? {} : { tooltip: buildTooltipBase(theme) }),
  }
}
